// DraaidagenOverzicht.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/nl';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { useMsal } from "@azure/msal-react";
import { Fab, Tooltip, Grid, Dialog, DialogContent, DialogTitle, DialogActions, List, ListItem, ListItemText, Button } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import DubbeleReserveringenDialog from './DubbeleReserveringenDialog';
import MismatchedReservations from './MismatchedReservations';
import { set } from 'lodash';
import { useMaterialReservation } from './MaterialReservationContext';
import { MaterialenOverzicht } from './MaterialenOverzicht';
import { Link } from 'react-router-dom';

// Custom Event component die een echte link rendert (voor "Open in nieuw tabblad")
const CustomEvent = ({ event }) => {
  const draaidagId = event.record?.id;
  
  return (
    <Tooltip 
      title={
        <div style={{ whiteSpace: 'pre-line', fontSize: '0.85rem' }}>
          {event.tooltipText || event.fullTitle || event.title}
        </div>
      }
      arrow 
      enterDelay={400}
      placement="top"
    >
      <Link 
        to={`/MaterialReservation?draaidagId=${draaidagId}`}
        style={{ 
          color: 'inherit', 
          textDecoration: 'none',
          display: 'block',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '0.85em',
          lineHeight: '1.3'
        }}
        onClick={(e) => {
          // Voorkom standaard link navigatie bij normale klik (wordt afgehandeld door onSelectEvent)
          // Maar sta Ctrl+Click en Middle-click toe voor nieuw tabblad
          if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
            e.preventDefault();
          }
        }}
      >
        {event.title}
      </Link>
    </Tooltip>
  );
};

moment.locale('nl-NL',{
	week: {
		dow: 1 //Monday is the first day of the week.
	}
}); // or 'nl' for Dutch

const localizer = momentLocalizer(moment);

const projectTypeColors = {
  "Video": "#FFD1DC",    // Pastel Pink
  "Testimonial": "#FFE4D4", // Pastel Peach
  "Interview": "#FFF4CC",  // Pastel Yellow
  "Registratie": "#DCFFD1",  // Pastel Green
  "Registratie met interviewer": "#D1FFEB",  // Pastel Turquoise
  "Aftermovie": "#D1F2FF",  // Pastel Blue
  "TV Commercial": "#DAD1FF",  // Pastel Lavender
  "Online Commercial": "#FFD1F4",  // Pastel Magenta
  "Animatiefilm": "#FFCCF2",  // Pastel Rose
  "Infographic": "#F2D1FF",  // Pastel Purple
  "Webinar": "#FFEBD1",  // Pastel Apricot
  "Explainer": "#F9D1FF",  // Pastel Mauve
  "Campagne": "#E8D1FF",  // Pastel Lilac
  "Podcast": "#FFD9D6",  // Pastel Coral
  "Vlog": "#FFE0D1",  // Pastel Tangerine
  "Fotografie": "#D9E9FF",  // Pastel Periwinkle
  // ... you can continue adding more mappings
};

// Definieer een array van kleurtoewijzingen
const eventColorExplanation = [
  { label: "Jouw draaidag, materiaal gereserveerd", color: "#8cbd4c" },
  { label: "Jouw draaidag, geen materiaal gereserveerd", color: "#de6a87" },
  { label: "Andere draaidag, materiaal gereserveerd", color: "#cbe7df" },
  { label: "Andere draaidag, geen materiaal gereserveerd", color: "#ead1c0" },
  { label: "Draaidag met dubbele reservering", color: "#FF0000" },
  { label: "📅-1 = Extra dag voor draaidag (voorbereiding)", icon: "📅-1" },
  { label: "📅+1 = Extra dag na draaidag (late terugkomst)", icon: "📅+1" },
  { label: "📅±1 = Extra dag voor én na draaidag", icon: "📅±1" },
];

// Persistent cache key
const DRAAIDAGEN_LOCALSTORAGE_KEY = 'draaidagenCache';

// Lees cache uit localStorage bij initialisatie
function getInitialDraaidagen() {
  try {
    const cached = localStorage.getItem(DRAAIDAGEN_LOCALSTORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && Array.isArray(parsed.data)) {
        return parsed.data;
      }
    }
  } catch (e) { console.warn('Failed to parse draaidagen cache from localStorage', e); }
  return [];
}

export const DraaidagenOverzicht = React.memo(function DraaidagenOverzicht() {
    const { selectedDraaidag, setSelectedDraaidag, fetchReservations, reservations, loading, error, initialDraaidagId } = useMaterialReservation();
    const [draaidagen, setDraaidagen] = useState(getInitialDraaidagen);
    const [draaidagenCache, setDraaidagenCache] = useState({ data: getInitialDraaidagen(), timestamp: null });
    const DRAAIDAGEN_CACHE_DURATION = 5 * 60 * 1000; // 5 minuten cache
    const [localLoading, setLocalLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedDateEvents, setSelectedDateEvents] = useState([]);
    const [email, setEmail] = useState(null);
    const [legendaOpen, setLegendaOpen] = useState(false); // legenda open/dicht
    const [currentView, setCurrentView] = useState('month'); // remember the view 
    
    // Onthoud welke maand de gebruiker bekijkt (persist in localStorage)
    const [currentDate, setCurrentDate] = useState(() => {
      try {
        const savedDate = localStorage.getItem('calendarCurrentDate');
        if (savedDate) {
          const parsed = new Date(savedDate);
          if (!isNaN(parsed.getTime())) return parsed;
        }
      } catch (e) { /* ignore */ }
      return new Date();
    }); 

    const [dubbeleReserveringenDialogOpen, setDubbeleReserveringenDialogOpen] = useState(false);

    const [draaidagenMetDubbeleReserveringen, setDraaidagenMetDubbeleReserveringen] = useState([]);
    const [toonAlertDubbeleReserveringen, setToonAlertDubbeleReserveringen] = useState(false);

    const { instance } = useMsal();
    
    const activeAccount = instance.getActiveAccount();

    // Fetch draaidagen from backend - alleen van 30 dagen geleden tot in de toekomst
    const fetchDraaidagenFromBackend = useCallback(async (startDateOverride) => {
      let cancelled = false;
      try {
        // Bepaal startdatum: 30 dagen geleden of override
        const today = new Date();
        const startDate = startDateOverride || new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        const startDateStr = startDate.toISOString().slice(0, 10);
        
        // Check cache first
        const now = Date.now();
        if (draaidagenCache.data && draaidagenCache.timestamp && 
            (now - draaidagenCache.timestamp) < DRAAIDAGEN_CACHE_DURATION) {
          setDraaidagen(draaidagenCache.data);
          setLocalLoading(false);
          return;
        }
        
        // Stop existing timer if it exists (try-catch to avoid warnings)
        try {
          console.timeEnd('fetchDraaidagen');
        } catch (e) {
          // Timer doesn't exist, ignore
        }
        console.time('fetchDraaidagen');
        const response = await axios.get(`${process.env.REACT_APP_CURRENT_URL}/Api/draaidagen?startDate=${startDateStr}`);
        if (!cancelled) {
          setDraaidagen(response.data);
          // Update cache
          setDraaidagenCache({ data: response.data, timestamp: now });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[FETCH] Error while fetching draaidagen from backend:', error);
        }
      } finally {
        if (!cancelled) {
          setLocalLoading(false);
        }
      }
      return () => { cancelled = true; };
    }, [draaidagenCache.timestamp]);

    // Memoize dubbele reserveringen identificatie - alleen voor relevante (toekomstige) draaidagen
    const identificeerDubbeleReserveringen = useCallback((reserveringen, draaidagenLijst) => {
      // Tel reserveringen per draaidag
      const draaidagCounts = reserveringen.reduce((acc, reservering) => {
        const draaidagIds = reservering.fields?.DraaidagOutlook;
        if (Array.isArray(draaidagIds)) {
          draaidagIds.forEach(draaidagId => {
            acc[draaidagId] = (acc[draaidagId] || 0) + 1;
          });
        }
        return acc;
      }, {});

      // Filter op draaidagen met meer dan 1 reservering
      const dubbeleDraaidagIds = Object.keys(draaidagCounts).filter(draaidagId => draaidagCounts[draaidagId] > 1);
      
      // Filter alleen draaidagen die in de toekomst liggen of vandaag zijn
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const relevanteDubbeleDraaidagen = dubbeleDraaidagIds.filter(draaidagId => {
        const draaidag = draaidagenLijst?.find(d => d.id === draaidagId);
        if (!draaidag?.fields?.StartDatum) return false;
        
        const startDatum = new Date(draaidag.fields.StartDatum);
        startDatum.setHours(0, 0, 0, 0);
        
        // Alleen tonen als de draaidag vandaag of in de toekomst is
        return startDatum >= today;
      });
      
      return relevanteDubbeleDraaidagen;
    }, []);

    // Consolideer initiele data fetching
    useEffect(() => {
      let cancelled = false;
      
      const initializeData = async () => {
        // Fetch draaidagen
        await fetchDraaidagenFromBackend();
        
        // Fetch reservations
        const reserveringen = await fetchReservations();
        
        // Note: draaidagen state is not yet updated here, so we skip dubbele check
        // It will be handled by the separate useEffect below
      };
      
      initializeData();
      
      return () => { cancelled = true; };
    }, [fetchDraaidagenFromBackend, fetchReservations]);

    useEffect(() => {
        fetchReservations();
    }, [fetchReservations]);

    // Check voor dubbele reserveringen wanneer beide data beschikbaar zijn
    useEffect(() => {
      if (draaidagen.length > 0 && reservations.length > 0) {
        const dubbeleDraaidagenIds = identificeerDubbeleReserveringen(reservations, draaidagen);
        setDraaidagenMetDubbeleReserveringen(dubbeleDraaidagenIds);
        setToonAlertDubbeleReserveringen(dubbeleDraaidagenIds.length > 0);
      }
    }, [draaidagen, reservations, identificeerDubbeleReserveringen]);

    // Auto-select draaidag from URL parameter (for "Open in new tab" support)
    useEffect(() => {
      if (initialDraaidagId && draaidagen.length > 0 && !selectedDraaidag) {
        const draaidag = draaidagen.find(d => d.id === initialDraaidagId);
        if (draaidag) {
          setSelectedDraaidag(draaidag);
        }
      }
    }, [initialDraaidagId, draaidagen, selectedDraaidag, setSelectedDraaidag]);

    const calculateDaysDifference = useCallback((start, end) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
  
      // Calculate difference in time
      const timeDifference = endDate.getTime() - startDate.getTime();
  
      // Calculate difference in days
      const daysDifference = timeDifference / (1000 * 3600 * 24) + 1; // Add 1 to include both start and end dates
  
      // Return appropriate string
      return daysDifference === 1 ? "1 dag" : `${daysDifference} dagen`;
    }, []);

    // Bepaal welke draaidagen een ExtraDagNa of ExtraDagVoor reservering hebben
    const draaidagenMetExtraDagen = useMemo(() => {
      const extraDagNaSet = new Set();
      const extraDagVoorSet = new Set();
      if (Array.isArray(reservations)) {
        reservations.forEach(res => {
          const draaidagId = res?.fields?.DraaidagOutlook?.[0];
          if (draaidagId) {
            if (res?.fields?.ExtraDagNa === true) {
              extraDagNaSet.add(draaidagId);
            }
            if (res?.fields?.ExtraDagVoor === true) {
              extraDagVoorSet.add(draaidagId);
            }
          }
        });
      }
      return { extraDagNa: extraDagNaSet, extraDagVoor: extraDagVoorSet };
    }, [reservations]);

    // Memoize events generation
    const events = useMemo(() => {
      const result = draaidagen.map(record => {
        // Set a default style
        let style = {};

        // Check if logged-in user is in the EmailCameraman array
        const isUserCameramanForEvent = record.fields.EmailCameraman 
            ? record.fields.EmailCameraman.includes(email)
            : false;

        if (isUserCameramanForEvent) {
          if ((record.fields.AantalMateriaalGereserveerd>0) || record.fields['Geen Materiaal Nodig']) {
              style = {
                  backgroundColor: '#8cbd4c', // Green for cameraman events with Aantal > 0
              };
          } else {
              style = {
                  backgroundColor: '#de6a87', // Orange for other cameraman events
              };
          }
        } else if ((record.fields.AantalMateriaalGereserveerd>0)  || record.fields['Geen Materiaal Nodig']) {
            
            style = {
                backgroundColor: '#cbe7df', // Light cbe7df when Aantal > 0 or GeenMateriaalNodig is true for non-cameraman
                color: '#000000' // Black for text
            };
        } else { // Default style for other conditions
          style = { 
                backgroundColor: '#ead1c0', // Light 
                color: '#000000' // Black for text
            };
        }
        
        if (draaidagenMetDubbeleReserveringen.includes(record.id)) {
            style = { 
                backgroundColor: '#FF0000', // Light 
                color: '#FFFFFF' // Black for text
            };
        }

        // Maak een leesbare korte titel voor de kalender
        const customNaam = record.fields.WeergaveNaamMateriaalApp;
        const originalTitle = record.fields.Draaidag || '';
        const fullTitle = customNaam || originalTitle;
        
        // Als er een custom weergavenaam is, gebruik die direct (heeft voorrang)
        let shortTitle = '';
        if (customNaam && customNaam.trim()) {
          shortTitle = customNaam;
        } else {
          // Anders: genereer titel uit crew + project + nummer
          // Haal crew uit het Crew veld (niet uit de titel)
          const crewField = record.fields.Camerapersonen;
          const crewNaam = Array.isArray(crewField) 
            ? crewField.join(' + ') 
            : (crewField || '');
          
          // Extraheer projectnaam en nummer uit de originele titel
          // Format is vaak: "Namen || Projectnaam - Extra info - [nummer] - Initialen"
          const projectMatch = originalTitle.match(/\|\|\s*([^[]+)/)?.[1]?.trim()?.replace(/\s*-\s*[A-Z]{2}\s*$/, '') || '';
          const projectNumMatch = originalTitle.match(/\[(\d+)\]/)?.[1] || '';
          
          // Maak korte titel: Crew: Projectnaam [nummer]
          if (crewNaam && projectMatch && projectNumMatch) {
            shortTitle = `${crewNaam}: ${projectMatch} [${projectNumMatch}]`;
          } else if (crewNaam && projectMatch) {
            shortTitle = `${crewNaam}: ${projectMatch}`;
          } else if (projectMatch && projectNumMatch) {
            shortTitle = `${projectMatch} [${projectNumMatch}]`;
          } else if (projectMatch) {
            shortTitle = projectMatch;
          } else if (crewNaam) {
            shortTitle = crewNaam;
          } else {
            shortTitle = fullTitle;
          }
        }
        
        // Voor tooltip: extraheer altijd projectnummer uit originele titel
        const projectNumMatch = originalTitle.match(/\[(\d+)\]/)?.[1] || '';
        
        // Voeg indicator VOORAAN toe voor extra dagen (visueel belangrijk)
        const heeftExtraDagVoor = draaidagenMetExtraDagen.extraDagVoor.has(record.id);
        const heeftExtraDagNa = draaidagenMetExtraDagen.extraDagNa.has(record.id);
        
        if (heeftExtraDagVoor && heeftExtraDagNa) {
          shortTitle = '📅±1 ' + shortTitle;
        } else if (heeftExtraDagVoor) {
          shortTitle = '📅-1 ' + shortTitle;
        } else if (heeftExtraDagNa) {
          shortTitle = '📅+1 ' + shortTitle;
        }
        
        // Bouw uitgebreide tooltip met alle info
        const tooltipParts = [];
        // Toon custom naam én originele titel in tooltip
        if (customNaam && originalTitle && customNaam !== originalTitle) {
          tooltipParts.push(`✏️ ${customNaam}`);
          tooltipParts.push(`📋 ${originalTitle}`);
        } else {
          tooltipParts.push(fullTitle);
        }
        if (projectNumMatch) tooltipParts.push(`Project #${projectNumMatch}`);
        if (record.fields.Aantal > 0) tooltipParts.push(`📦 ${record.fields.Aantal} materialen`);
        if (heeftExtraDagVoor) tooltipParts.push('📅-1 Extra dag vóór');
        if (heeftExtraDagNa) tooltipParts.push('📅+1 Extra dag na');
        const tooltipText = tooltipParts.join('\n');
  
        const startDate = new Date(record.fields.StartDatum);
        const endDate = new Date(record.fields.EindDatum);

        // Voor react-big-calendar moet de einddatum van een 'allDay' event
        // op de *start* van de VOLGENDE dag worden gezet om correct over meerdere dagen te renderen.
        if (endDate.getTime() > startDate.getTime()) {
            endDate.setDate(endDate.getDate() + 1);
        }

        return {
          title: shortTitle,
          fullTitle: fullTitle, // Volledige titel
          tooltipText: tooltipText, // Uitgebreide tooltip met alle details
          start: startDate,
          end: endDate,
          style: style,
          allDay: true,
          record: record 
        };
      });
      return result;
    }, [draaidagen, email, draaidagenMetDubbeleReserveringen, draaidagenMetExtraDagen]);
    
    // Memoize handlers
    const handleDrillDown = useCallback((date) => {
      // Filter the events of the clicked date
      const eventsOnDate = events.filter(event => {
        const eventDate = new Date(event.start);
        return eventDate.toDateString() === date.toDateString();
      });
    
      setSelectedDateEvents(eventsOnDate);
      setDialogOpen(true);
    }, [events]);

    const handleViewChange = useCallback((view) => {
      setCurrentView(view);
    }, []);

    // Onthoud de huidige datum wanneer de gebruiker navigeert
    const handleNavigate = useCallback((newDate) => {
      setCurrentDate(newDate);
      try {
        localStorage.setItem('calendarCurrentDate', newDate.toISOString());
      } catch (e) { /* ignore */ }
    }, []);

    const handleMismatchedReservationClick = useCallback(async (reservation) => {

      // Guard: check if reservation is an object with fields (not just an ID string)
      if (!reservation?.fields) {
        return;
      }

      if (reservation.fields.DraaidagOutlook?.[0]) {
        const draaidag = draaidagen.find(draaidag => draaidag.id === reservation.fields.DraaidagOutlook[0]);
        setSelectedDraaidag(draaidag);
      }
    }, [draaidagen, setSelectedDraaidag]);

    const handleDraaidagClick = useCallback(async(draaidag) => {
      
      // Simply set the selected draaidag
      setSelectedDraaidag(draaidag);
    }, [setSelectedDraaidag]);

    const handleCloseDialog = useCallback(() => setDialogOpen(false), []);
    const handleCloseLegenda = useCallback(() => setLegendaOpen(false), []);
    const handleOpenLegenda = useCallback(() => setLegendaOpen(true), []);
    const handleCloseDubbeleReserveringen = useCallback(() => setDubbeleReserveringenDialogOpen(false), []);

    // Delete handler for mismatched reservations
    const handleDeleteMismatchedReservation = useCallback(async (reservationId) => {
      try {
        await axios.delete(`${process.env.REACT_APP_CURRENT_URL}/Api/reservations/${reservationId}`);
        // Refresh reservations after delete
        fetchReservations(true);
        alert("Reservering succesvol verwijderd");
      } catch (error) {
        console.error("Error deleting reservation:", error);
        alert("Er is een fout opgetreden bij het verwijderen van de reservering");
      }
    }, [fetchReservations]);
    
    // Verrijk reservations met draaidagdata voor MismatchedReservations
    const reservationsWithDraaidagData = useMemo(() => {
      if (!Array.isArray(reservations) || !Array.isArray(draaidagen)) return reservations;
      return reservations.map(res => {
        const draaidagId = res.fields.DraaidagOutlook?.[0];
        const draaidag = draaidagId ? draaidagen.find(d => d.id === draaidagId) : null;
        return {
          ...res,
          fields: {
            ...res.fields,
            OrigineleDraaidagNaam: draaidag?.fields?.Draaidag || undefined,
            startDatumDraaidag: draaidag?.fields?.StartDatum || undefined,
            eindDatumDraaidag: draaidag?.fields?.EindDatum || undefined,
          }
        };
      });
    }, [reservations, draaidagen]);

    // Lokale update van een draaidag (voor snelle UI)
    const updateDraaidagLocally = useCallback((updatedDraaidag) => {
      setDraaidagen(prev =>
        prev.map(d => d.id === updatedDraaidag.id ? { ...d, fields: { ...d.fields, ...updatedDraaidag.fields } } : d)
      );
    }, []);

    useEffect(() => {
      if (draaidagenCache.data && draaidagen.length === 0) {
        setDraaidagen(draaidagenCache.data);
      }
    }, [draaidagenCache.data]);

    // Sla cache op in localStorage bij elke update
    useEffect(() => {
      if (draaidagenCache.data) {
        try {
          localStorage.setItem(DRAAIDAGEN_LOCALSTORAGE_KEY, JSON.stringify(draaidagenCache));
        } catch (e) { console.warn('Failed to save draaidagen cache to localStorage', e); }
      }
    }, [draaidagenCache]);

    return (
      <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '100vh' }}>
        <Grid item xs={12} md={10} lg={10}>
          {toonAlertDubbeleReserveringen && (
            <div style={{ backgroundColor: "#ffcccb", padding: "10px", marginTop: "20px", marginBottom: "20px", borderRadius: "5px", border: "1px solid red" }}>
              <strong>Let op:</strong> Er zijn draaidagen met dubbele reserveringen (rood gemarkeerd). Graag deze corrigeren door de draaidag te selecteren en de onjuiste reservering te verwijderen. Check ook <a href='https://airtable.com/apphMT5j6gtUnMY1p/pagWOK6C02DDm7YCt' target="_blank" rel="noopener noreferrer">Airtable Interface</a>
            </div>
          )}
          <Dialog 
            open={dialogOpen} 
            onClose={handleCloseDialog}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 2 }
            }}
          >
            <DialogTitle sx={{ 
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              📅 Draaidagen op {selectedDateEvents[0] && moment(selectedDateEvents[0].start).format('dddd D MMMM YYYY')}
              <span style={{ 
                marginLeft: 'auto', 
                fontSize: '0.9rem', 
                backgroundColor: '#e3f2fd',
                padding: '2px 8px',
                borderRadius: '12px',
                color: '#1976d2'
              }}>
                {selectedDateEvents.length} {selectedDateEvents.length === 1 ? 'draaidag' : 'draaidagen'}
              </span>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <List disablePadding>
                {selectedDateEvents.map((event, index) => {
                  const record = event.record;
                  const fields = record?.fields || {};
                  const hasReservation = reservations?.some(r => r?.fields?.IDdraaidagOutlook?.[0] === record?.id);
                  // Camerapersonen kan een string of array zijn (Airtable linked records)
                  const camerapersonen = Array.isArray(fields.Camerapersonen) 
                    ? fields.Camerapersonen.join(' ') 
                    : (fields.Camerapersonen || '');
                  const isMyDraaidag = camerapersonen.toLowerCase().includes(email?.split('@')[0]?.toLowerCase() || '');
                  
                  // Extraheer crew uit titel
                  const crewMatch = (fields.Draaidag || '').match(/^([^|]+)/)?.[1]?.trim() || '';
                  // Extraheer project
                  const projectMatch = (fields.Draaidag || '').match(/\|\|\s*([^-\[]+)/)?.[1]?.trim() || '';
                  const projectNum = (fields.Draaidag || '').match(/\[(\d+)\]/)?.[1] || '';
                  
                  return (
                    <ListItem 
                      key={record.id}
                      button 
                      onClick={() => {
                        setSelectedDraaidag(record);
                        handleCloseDialog();
                      }}
                      sx={{ 
                        backgroundColor: event.style?.backgroundColor || '#f5f5f5',
                        borderRadius: 1,
                        mb: 1,
                        border: '1px solid',
                        borderColor: isMyDraaidag ? '#4caf50' : 'transparent',
                        '&:hover': {
                          filter: 'brightness(0.95)',
                          transform: 'scale(1.01)',
                          transition: 'all 0.2s'
                        }
                      }}
                    >
                      <ListItemText 
                        primary={
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: '1rem' }}>
                              {projectMatch || event.title}
                            </strong>
                            {projectNum && (
                              <span style={{ 
                                fontSize: '0.75rem', 
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                padding: '2px 6px',
                                borderRadius: '4px'
                              }}>
                                #{projectNum}
                              </span>
                            )}
                            {hasReservation ? (
                              <span style={{ fontSize: '0.75rem', color: '#2e7d32' }}>✅ Gereserveerd</span>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#d32f2f' }}>⚠️ Geen reservering</span>
                            )}
                          </div>
                        }
                        secondary={
                          <div style={{ marginTop: 4 }}>
                            {crewMatch && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem' }}>
                                👥 <span>{crewMatch}</span>
                              </div>
                            )}
                            {fields.Aantal > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', color: '#666' }}>
                                📦 {fields.Aantal} materialen
                              </div>
                            )}
                          </div>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </DialogContent>

            <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
              <Button onClick={handleCloseDialog} variant="outlined">
                Sluiten
              </Button>
            </DialogActions>
          </Dialog>

          {/* Legenda  with floating button*/}
          <Fab 
            color="primary" 
            aria-label="info" 
            style={{ position: 'absolute', bottom: 16, right: 16 }}
            onClick={handleOpenLegenda} // setLegendaOpen is een state setter die de legenda open/dicht doet
          >
            <Tooltip title="Legenda">
              <InfoIcon />
            </Tooltip>
          </Fab>

          {/* Legenda Dialog */}
          <Dialog open={legendaOpen} onClose={handleCloseLegenda}>
            <DialogTitle>Legenda</DialogTitle>
            <DialogContent>
              <List>
                {eventColorExplanation.map((item) => (
                  <ListItem key={item.label}>
                    <ListItemText primary={item.label} />
                    {item.color ? (
                      <div style={{ width: 24, height: 24, backgroundColor: item.color, borderRadius: '50%', marginLeft: 10 }} />
                    ) : item.icon ? (
                      <span style={{ marginLeft: 10, fontSize: '1.2em' }}>{item.icon}</span>
                    ) : null}
                  </ListItem>
                ))}
              </List>
            </DialogContent>
          </Dialog>
          <DubbeleReserveringenDialog
            key={dubbeleReserveringenDialogOpen}
            open={dubbeleReserveringenDialogOpen}
            onClose={handleCloseDubbeleReserveringen}
            reserveringen={draaidagenMetDubbeleReserveringen}
            handleDeleteReservation={handleDraaidagClick}
          />
          <MismatchedReservations 
            draaidag={draaidagen} 
            handleDeleteReservation={handleDeleteMismatchedReservation}
            reservationsData={reservationsWithDraaidagData}
            onReservationClick={handleMismatchedReservationClick}
            setGeselecteerdeDraaidag={setSelectedDraaidag}
          />
          {selectedDraaidag ? (
            <MaterialenOverzicht updateDraaidagLocally={updateDraaidagLocally} />
          ) : (
            <div style={{ marginTop: '5px' }}>
              <Calendar
                showMultiDayTimes 
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 'calc(100vh - 200px)', minHeight: 400, maxHeight: 800 }}
                views={[ 'month', 'work_week', 'agenda']}            
                view={currentView}
                date={currentDate}
                onView={handleViewChange}
                onNavigate={handleNavigate}
                onSelectEvent={event => handleDraaidagClick(event.record)}
                onDrillDown={handleDrillDown}
                components={{
                  event: CustomEvent
                }}
                eventPropGetter={(event, start, end, isSelected) => {
                  const draaidagId = event.record?.id;
                  const reservationCount = Array.isArray(reservations) && draaidagId ? 
                      reservations.filter(r => r?.fields?.IDdraaidagOutlook?.[0] === draaidagId).length : 0;
                  
                  // Use the style from the event if available
                  if (event.style) {
                      return { style: event.style };
                  }
                  
                  // Fallback style
                  let backgroundColor = '#3174ad'; // Default blue
                  if (reservationCount >= 1) {
                      backgroundColor = '#5cb85c'; // Green
                  }
                  return { style: { backgroundColor } };
                }}           

              />
            </div>
          )}
        </Grid>
      </Grid>
    );
    
  });
